import React from 'react';
import {
    Typography,
    Box,
    Tooltip,
    IconButton,
    Chip
} from '@mui/material';
import MetricCard from '../shared/MetricCard';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
    Info as InfoIcon,
    OpenInNew as DrillDownIcon
} from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line } from 'recharts';

/**
 * Enhanced KPI Card Component
 * 
 * Industry-standard PSA KPI card with:
 * - Current value with appropriate formatting
 * - Comparison against target/prior period/benchmark
 * - Trend indicator with percentage change
 * - Optional sparkline mini-chart
 * - Click-to-drill-down capability
 * - Tooltip explaining the metric
 * 
 * @param {Object} props
 * @param {string} props.title - KPI name
 * @param {number|string} props.value - Current value
 * @param {'currency'|'percentage'|'number'|'days'} props.format - Display format
 * @param {Object} props.comparison - Comparison data { value, label, type }
 * @param {Object} props.trend - Trend data { direction, percentage, isPositive }
 * @param {number[]} props.sparklineData - Data points for sparkline
 * @param {boolean} props.showSparkline - Whether to show sparkline
 * @param {Function} props.onClick - Drill-down handler
 * @param {string} props.tooltip - Metric explanation
 * @param {'good'|'warning'|'critical'} props.status - Health status
 * @param {string} props.statusLabel - Optional chip label (overrides capitalized status)
 * @param {boolean} props.loading - Loading state
 * @param {string} props.testId - Test ID for testing
 * @param {string} props.kpiKey - Canonical KPI key for diagnostics and parity checks
 * @param {string} props.dataSource - Source tag for KPI diagnostics
 */
const EnhancedKPICard = ({
    title,
    value,
    format = 'number',
    comparison = null,
    trend = null,
    sparklineData = [],
    showSparkline = false,
    onClick = null,
    tooltip = null,
    status = null,
    statusLabel = null,
    loading = false,
    testId = null,
    kpiKey = null,
    dataSource = null,
}) => {
    // Format value based on type
    const formatValue = (val, fmt) => {
        if (val === null || val === undefined) return 'N/A';
        
        switch (fmt) {
            case 'currency':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    notation: 'standard',
                    maximumFractionDigits: Math.abs(val) >= 1000 ? 0 : 2
                }).format(val);
            
            case 'percentage':
                // KPI percentages are expected to arrive pre-scaled from backend contracts.
                // Formatter is now presentation-only and does not normalize scale.
                {
                    const percentageValue = Number(val);
                    if (!Number.isFinite(percentageValue)) {
                        return 'N/A';
                    }
                    return `${percentageValue.toFixed(1)}%`;
                }
            
            case 'days':
                return `${Math.round(val)} days`;
            
            case 'number':
            default:
                return new Intl.NumberFormat('en-US', {
                notation: 'standard',
                    maximumFractionDigits: 1
                }).format(val);
        }
    };

    // Get status color
    const getStatusColor = (statusValue) => {
        switch (statusValue) {
            case 'good': return 'success.main';
            case 'warning': return 'warning.main';
            case 'critical': return 'error.main';
            default: return 'primary.main';
        }
    };

    // Get status background color
    const getStatusBgColor = (statusValue) => {
        switch (statusValue) {
            case 'good': return 'success.light';
            case 'warning': return 'warning.light';
            case 'critical': return 'error.light';
            default: return 'primary.light';
        }
    };

    // Get trend icon and color
    const getTrendDisplay = () => {
        if (!trend) return null;
        
        const { direction, percentage, isPositive } = trend;
        const color = isPositive ? 'success.main' : 'error.main';
        
        let Icon;
        switch (direction) {
            case 'up': Icon = TrendingUpIcon; break;
            case 'down': Icon = TrendingDownIcon; break;
            default: Icon = TrendingFlatIcon;
        }
        
        const formattedPct = percentage >= 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon sx={{ fontSize: 16, color }} />
                <Typography variant="caption" sx={{ color, fontWeight: 'bold' }}>
                    {formattedPct}
                </Typography>
            </Box>
        );
    };

    // Get sparkline color based on trend
    const getSparklineColor = () => {
        if (trend?.isPositive === true) return '#4caf50';
        if (trend?.isPositive === false) return '#f44336';
        return '#2196f3';
    };

    const headerAdornment = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                    <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                </Tooltip>
            )}
            {onClick && (
                <Tooltip title="Click for details">
                    <IconButton size="small" sx={{ p: 0.25 }} aria-label="Open metric details">
                        <DrillDownIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );

    const subtitle = comparison
        ? `${comparison.label}: ${formatValue(comparison.value, format)}`
        : null;

    const trendNode = getTrendDisplay();

    const statusChip = (statusLabel || status) ? (
        <Chip
            label={statusLabel || `${status.charAt(0).toUpperCase()}${status.slice(1)}`}
            size="small"
            sx={{
                bgcolor: getStatusBgColor(status),
                color: getStatusColor(status),
                fontWeight: 'bold',
                fontSize: '0.65rem',
                height: 20,
                mb: 1,
            }}
        />
    ) : null;

    const sparklineNode = showSparkline && sparklineData.length > 0 ? (
        <Box sx={{ mt: 1, height: 30 }} role="img" aria-label="90 day trend">
            <ResponsiveContainer width="100%" height={30}>
                <LineChart
                    data={sparklineData.map((point, index) => ({ value: point, index }))}
                    margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                >
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={getSparklineColor()}
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    ) : null;

    return (
        <MetricCard
            title={title}
            value={formatValue(value, format)}
            subtitle={subtitle}
            testId={testId}
            kpiKey={kpiKey}
            dataSource={dataSource}
            color={status === 'good' ? 'success' : status === 'warning' ? 'warning' : status === 'critical' ? 'error' : 'primary'}
            availability={loading ? 'loading' : undefined}
            onClick={onClick}
            trend={trendNode}
            headerAdornment={headerAdornment}
        >
            {statusChip}
            {sparklineNode}
        </MetricCard>
    );
};

export default EnhancedKPICard;

